import { Link } from "react-router-dom";
import { Gauge, Ship, Hash, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageSlot } from "@/components/ImageSlot";
import { primaryPhoto, formatPrice, STATUS_LABELS, STATUS_COLORS } from "@/lib/api";

export function BoatCard({ boat, index = 0 }) {
  const photo = primaryPhoto(boat, 800);
  const isSold = boat.status === "sold";
  const salePrice = boat.promo_price || boat.price;
  const hasPromo = boat.promo_price && boat.price && boat.promo_price < boat.price;

  return (
    <Link
      to={`/boats/${boat.slug}`}
      className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`boat-card-${boat.slug}`}
    >
      <div className="relative">
        {photo ? (
          <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">
            <img src={photo} alt={boat.photos?.find((p)=>p.is_primary)?.alt_text || `${boat.year} ${boat.brand} ${boat.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          </div>
        ) : (
          <ImageSlot label={`${boat.year}-${boat.brand}-${boat.model}-${boat.stock_number}-001.jpg`} dimensions="1200 × 900" />
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${boat.condition === "new" ? "bg-mtm-purple text-white" : "bg-slate-800 text-white"}`}>
            {boat.condition === "new" ? "New" : "Used"}
          </span>
          <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${STATUS_COLORS[boat.status] || "bg-slate-500 text-white"}`}>
            {STATUS_LABELS[boat.status]}
          </span>
        </div>
        {isSold && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-red-600 text-white font-heading font-extrabold text-xl px-6 py-2 rounded-md -rotate-6 shadow-lg" data-testid="sold-status-badge">SOLD</span>
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-bold text-lg text-slate-900 leading-tight">{boat.year} {boat.brand} {boat.model}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-xs text-slate-600 font-mono">
          {boat.engine_make && <span className="flex items-center gap-1"><Gauge size={13} className="text-mtm-teal" /> {boat.engine_make} {boat.horsepower && `${boat.horsepower}HP`}</span>}
          {boat.boat_type && <span className="flex items-center gap-1"><Ship size={13} className="text-mtm-teal" /> {boat.boat_type}</span>}
          {boat.stock_number && <span className="flex items-center gap-1"><Hash size={13} className="text-mtm-teal" /> {boat.stock_number}</span>}
        </div>
        <div className="mt-auto pt-4 flex items-end justify-between">
          <div>
            {boat.price_hidden || salePrice == null ? (
              <span className="text-sm font-semibold text-mtm-purple">Call for Price</span>
            ) : (
              <div className="flex flex-col">
                {hasPromo && <span className="text-xs text-slate-400 line-through">{formatPrice(boat.price)}</span>}
                <span className="font-heading font-extrabold text-xl text-mtm-purple-deep">{formatPrice(salePrice)}</span>
              </div>
            )}
          </div>
          <span className="flex items-center gap-1 text-sm font-semibold text-mtm-teal-dark group-hover:gap-2 transition-all">
            View Boat <ArrowRight size={15} />
          </span>
        </div>
      </div>
    </Link>
  );
}
